import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useLazyGetConversationMessagesQuery,
  useLazyGetResolvedSuggestionsQuery,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { useBranding } from "@targeted-support/ui/contexts";
import { getBenchmarkCasesForOrganisation } from "./benchmarkCases";
import { getSituationOptionsForOrganisation, getSuggestionOptionsForOrganisation } from "./benchmarkConstants";
import { computeMetrics } from "./metrics";
import { useBenchmarkHistory } from "./useBenchmarkHistory";
import {
  DEFAULT_MAX_CONCURRENT,
  DEFAULT_MAX_MESSAGES,
  useBenchmarkRunner,
} from "./useBenchmarkRunner";
import {
  CustomCasesView,
  HistoryView,
  ResultsView,
  RunningView,
  SelectionView,
} from "./components";
import type {
  BenchmarkCase,
  BenchmarkMetrics,
  CaseRunResult,
  StoredBenchmarkRun,
  StoredCaseResult,
  StoredCustomCase,
} from "./types";
import type { PreviousConversation } from "../test/TestPage2";

interface BenchmarkPageProps {
  navigateToTest: (previousConversation: PreviousConversation) => void;
}

type ViewState = "selection" | "running" | "results" | "history";

export const BenchmarkPage = ({ navigateToTest }: BenchmarkPageProps) => {
  const { tenantId } = useBranding();
  const benchmarkCases = useMemo(
    () => getBenchmarkCasesForOrganisation(tenantId),
    [tenantId],
  );
  const situationOptions = useMemo(
    () => getSituationOptionsForOrganisation(tenantId),
    [tenantId],
  );
  const suggestionOptions = useMemo(
    () => getSuggestionOptionsForOrganisation(tenantId),
    [tenantId],
  );

  const [selectedCaseIds, setSelectedCaseIds] = useState<Set<number>>(
    () => new Set(getBenchmarkCasesForOrganisation(tenantId).map((c) => c.id)),
  );

  useEffect(() => {
    setSelectedCaseIds(new Set(benchmarkCases.map((c) => c.id)));
  }, [benchmarkCases]);

  const [maxConcurrent, setMaxConcurrent] = useState(DEFAULT_MAX_CONCURRENT);
  const [maxMessages, setMaxMessages] = useState(DEFAULT_MAX_MESSAGES);
  const [shouldTerminateEarly, setShouldTerminateEarly] = useState(false);
  const [viewState, setViewState] = useState<ViewState>("selection");
  const [finalResults, setFinalResults] = useState<CaseRunResult[]>([]);
  const [finalMetrics, setFinalMetrics] = useState<BenchmarkMetrics | null>(
    null,
  );
  const [viewingHistoryRun, setViewingHistoryRun] =
    useState<StoredBenchmarkRun | null>(null);

  const [isHydrating, setIsHydrating] = useState(false);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [activeCases, setActiveCases] = useState<BenchmarkCase[]>([]);
  const [customFormCases, setCustomFormCases] = useState<BenchmarkCase[] | undefined>(undefined);

  const runner = useBenchmarkRunner();
  const history = useBenchmarkHistory();
  const [fetchConversationMessages] = useLazyGetConversationMessagesQuery();
  const [getResolvedSuggestions] = useLazyGetResolvedSuggestionsQuery();

  const selectedCases = useMemo(
    () => benchmarkCases.filter((c) => selectedCaseIds.has(c.id)),
    [selectedCaseIds, benchmarkCases],
  );

  const toggleCase = (id: number) => {
    setSelectedCaseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedCaseIds.size === benchmarkCases.length) {
      setSelectedCaseIds(new Set());
    } else {
      setSelectedCaseIds(new Set(benchmarkCases.map((c) => c.id)));
    }
  };

  const toStoredResult = (result: CaseRunResult): StoredCaseResult => {
    const { actualSuggestions, isPass, expectsNone, ...stored } = result;
    return stored;
  };

  const toStoredCustomCase = (c: BenchmarkCase): StoredCustomCase => ({
    id: c.id,
    ...c.modifiedCase,
  });

  const fromStoredCustomCase = (sc: StoredCustomCase): BenchmarkCase => {
    const { id, ...definition } = sc;
    return { id, originalCase: { ...definition }, modifiedCase: { ...definition }, comments: "" };
  };

  const hydrateStoredResults = useCallback(
    async (storedResults: StoredCaseResult[], customCases?: StoredCustomCase[]): Promise<CaseRunResult[]> => {
      const sourceCases = customCases ? customCases.map(fromStoredCustomCase) : benchmarkCases;
      const caseById = new Map(sourceCases.map((c) => [c.id, c]));
      return Promise.all(
        storedResults.map(async (stored) => {
          const benchmarkCase = caseById.get(stored.caseId);
          const expectsNone = !benchmarkCase?.modifiedCase.expectedSuggestion;

          if (!stored.sessionId || stored.status === "failed") {
            return { ...stored, actualSuggestions: [], isPass: false, expectsNone };
          }

          const { data } = await getResolvedSuggestions(stored.sessionId);
          const actualSuggestions = data?.resolved_suggestions ?? [];
          const isPass = expectsNone
            ? actualSuggestions.length === 0
            : actualSuggestions.some(
              (s) => s.toLowerCase() === benchmarkCase!.modifiedCase.expectedSuggestion.toLowerCase(),
            );

          return { ...stored, actualSuggestions, isPass, expectsNone };
        }),
      );
    },
    [getResolvedSuggestions, benchmarkCases],
  );

  const handleRun = async () => {
    if (selectedCases.length === 0) return;
    setActiveCases(selectedCases);
    setViewState("running");
    const result = await runner.run(selectedCases, maxConcurrent, maxMessages, shouldTerminateEarly);
    if (result) {
      setFinalResults(result.results);
      setFinalMetrics(result.metrics);
      history.saveRun(result.storedRun);
    }
    setViewState("results");
  };

  const handleCancel = () => {
    runner.cancel();
    const partialResults = runner.results;
    const partialMetrics = computeMetrics(partialResults);
    setFinalResults(partialResults);
    setFinalMetrics(partialMetrics);

    const isCustom = activeCases.some((c) => c.id < 0);
    if (partialResults.length > 0) {
      const storedRun: StoredBenchmarkRun = {
        id: `run-${Date.now()}`,
        timestamp: Date.now(),
        maxConcurrent,
        maxMessages,
        shouldTerminateEarly,
        caseIds: activeCases.map((c) => c.id),
        results: partialResults.map(toStoredResult),
        ...(isCustom ? { customCases: activeCases.map(toStoredCustomCase) } : {}),
      };
      history.saveRun(storedRun);
    }
    setViewState("results");
  };

  const handleReset = () => {
    const isCustom = activeCases.some((c) => c.id < 0) || !!viewingHistoryRun?.customCases;

    if (isCustom) {
      const sourceCases = viewingHistoryRun?.customCases
        ? viewingHistoryRun.customCases.map(fromStoredCustomCase)
        : activeCases;
      setCustomFormCases(sourceCases);
      setIsCustomMode(true);
    } else {
      const caseIds = activeCases.length > 0
        ? activeCases.map((c) => c.id)
        : (viewingHistoryRun?.caseIds ?? []);
      setSelectedCaseIds(new Set(caseIds));
    }

    setViewState("selection");
    setFinalResults([]);
    setFinalMetrics(null);
    setViewingHistoryRun(null);
    setActiveCases([]);
  };

  const handleBackToHistory = () => {
    setViewState("history");
    setFinalResults([]);
    setFinalMetrics(null);
    setViewingHistoryRun(null);
    setActiveCases([]);
  };

  const handleCustomRun = async (cases: BenchmarkCase[]) => {
    if (cases.length === 0) return;
    setActiveCases(cases);
    setViewState("running");
    const result = await runner.run(cases, maxConcurrent, maxMessages, shouldTerminateEarly);
    if (result) {
      setFinalResults(result.results);
      setFinalMetrics(result.metrics);
      history.saveRun(result.storedRun);
    }
    setViewState("results");
  };

  const handleCopyToCustomRun = useCallback(() => {
    const storedCustom = viewingHistoryRun?.customCases;
    const hydratedCustom = storedCustom ? storedCustom.map(fromStoredCustomCase) : [];
    const sourceCases = hydratedCustom.length > 0 ? hydratedCustom : activeCases;
    const casesToCopy = sourceCases.length > 0 ? sourceCases : benchmarkCases.filter((c) => finalResults.some((r) => r.caseId === c.id));
    setCustomFormCases(casesToCopy);
    setIsCustomMode(true);
    setViewState("selection");
    setFinalResults([]);
    setFinalMetrics(null);
    setViewingHistoryRun(null);
  }, [viewingHistoryRun, activeCases, finalResults, benchmarkCases]);

  const handleViewConversation = useCallback(
    async (sessionId: string) => {
      if (!sessionId) return;
      const { data } = await fetchConversationMessages(sessionId);
      if (!data) return;
      navigateToTest({
        sessionId,
        sessionVersionId: data.session_version_id,
        messages: data.messages ?? [],
        isComplete: data.is_complete ?? false,
        shouldTerminateEarly: data.should_terminate_early ?? false,
      });
    },
    [fetchConversationMessages, navigateToTest],
  );

  const handleViewHistoryRun = useCallback(
    async (run: StoredBenchmarkRun) => {
      setViewingHistoryRun(run);
      setActiveCases(run.customCases ? run.customCases.map(fromStoredCustomCase) : []);
      setIsHydrating(true);
      setViewState("results");

      const hydratedResults = await hydrateStoredResults(run.results, run.customCases);
      const metrics = computeMetrics(hydratedResults);

      setFinalResults(hydratedResults);
      setFinalMetrics(metrics);
      setIsHydrating(false);
    },
    [hydrateStoredResults],
  );

  useEffect(() => {
    if (viewState !== "running") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [viewState]);

  if (viewState === "running") {
    return (
      <RunningView
        cases={activeCases}
        caseStatuses={runner.caseStatuses}
        caseMessageCounts={runner.caseMessageCounts}
        caseSessionIds={runner.caseSessionIds}
        results={runner.results}
        currentCaseIndex={runner.currentCaseIndex}
        totalCases={runner.totalCases}
        onCancel={handleCancel}
        onViewConversation={handleViewConversation}
        shouldTerminateEarly={shouldTerminateEarly}
      />
    );
  }

  if (viewState === "results" && (finalMetrics || isHydrating)) {
    const displayCases = activeCases.length > 0 ? activeCases : benchmarkCases;
    return (
      <ResultsView
        results={finalResults}
        metrics={finalMetrics}
        cases={displayCases}
        onReset={handleReset}
        onBackToHistory={handleBackToHistory}
        onCopyToCustomRun={handleCopyToCustomRun}
        onViewConversation={handleViewConversation}
        historyRun={viewingHistoryRun}
        isLoading={isHydrating}
        shouldTerminateEarly={viewingHistoryRun ? !!viewingHistoryRun.shouldTerminateEarly : shouldTerminateEarly}
      />
    );
  }

  if (viewState === "history") {
    return (
      <HistoryView
        runs={history.runs}
        onViewRun={handleViewHistoryRun}
        onDeleteRun={history.deleteRun}
        onBack={() => setViewState("selection")}
        hydrateResults={hydrateStoredResults}
      />
    );
  }

  const toggleMode = () => {
    setIsCustomMode((prev) => !prev);
    setCustomFormCases(undefined);
  };

  if (isCustomMode) {
    return (
      <CustomCasesView
        initialCases={customFormCases}
        maxConcurrent={maxConcurrent}
        maxMessages={maxMessages}
        shouldTerminateEarly={shouldTerminateEarly}
        isCustomMode={isCustomMode}
        situationOptions={situationOptions}
        suggestionOptions={suggestionOptions}
        onToggleMode={toggleMode}
        onSetMaxConcurrent={setMaxConcurrent}
        onSetMaxMessages={setMaxMessages}
        onSetShouldTerminateEarly={setShouldTerminateEarly}
        onRun={handleCustomRun}
      />
    );
  }

  return (
    <SelectionView
      cases={benchmarkCases}
      selectedCaseIds={selectedCaseIds}
      maxConcurrent={maxConcurrent}
      maxMessages={maxMessages}
      shouldTerminateEarly={shouldTerminateEarly}
      isCustomMode={isCustomMode}
      onToggleMode={toggleMode}
      onToggleCase={toggleCase}
      onToggleAll={toggleAll}
      onSetMaxConcurrent={setMaxConcurrent}
      onSetMaxMessages={setMaxMessages}
      onSetShouldTerminateEarly={setShouldTerminateEarly}
      onRun={handleRun}
      onViewHistory={() => setViewState("history")}
      historyCount={history.runs.length}
      selectedCount={selectedCases.length}
    />
  );
};
